import { Button, MagellanPopup } from 'components/atoms';
import { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { PiCheck, PiWarning, PiX } from 'react-icons/pi';
import Popup from 'reactjs-popup';
import { PopupActions } from 'reactjs-popup/dist/types';
import { BUTTON_VARIANT } from 'types/components/button/button.interface';

interface ICriticalWarningPopupProps {
  open?: boolean;
  onClose?: () => void;
  onConfirm?: (actions: PopupActions) => void;
  confirmButtonVariant?: BUTTON_VARIANT;
  title: string;
  message: string;
  trigger?: JSX.Element;
  confirmButtonText?: string;
  cancelButtonText?: string;
  closeButtonText?: string;
  questionText?: string;
}

const CriticalWarningPopup = ({
  open,
  onClose,
  onConfirm,
  confirmButtonVariant = BUTTON_VARIANT.submit,
  title,
  message,
  trigger,
  confirmButtonText,
  cancelButtonText,
  closeButtonText,
  questionText,
}: ICriticalWarningPopupProps) => {
  const { t } = useTranslation();
  const ref = useRef<PopupActions>(null);

  const confirmLabel = confirmButtonText ?? t('popup.approve');
  const cancelLabel = cancelButtonText ?? t('popup.cancel');
  const closeLabel = closeButtonText ?? t('popup.close');
  const questionLabel = questionText ?? t('popup.question');

  return (
    <Popup
      ref={ref}
      modal
      nested
      open={open}
      onClose={onClose}
      trigger={trigger ?? <div style={{ display: 'none' }} />}>
      <div className='flex items-center justify-center w-screen h-screen bg-[#ff2700] text-white'>
        <MagellanPopup
          style={{ backgroundColor: 'transparent', borderRadius: 0 }}>
          <MagellanPopup.Header className='critical-warning-header'>
            <PiWarning />
            <span
              className='font-semibold'
              style={{ fontSize: '20px', lineHeight: '0.8' }}>
              {title}
            </span>
          </MagellanPopup.Header>
          <div className='flex flex-col opacity-80 max-w-96 text-center gap-4'>
            <p>{message}</p>
            {onConfirm && <p>{questionLabel}</p>}
          </div>
          <div className='flex flex-col gap-2 mt-4 critical-warning-buttons'>
            {onConfirm && (
              <Button
                variant={confirmButtonVariant}
                onClick={() =>
                  onConfirm && ref.current && onConfirm(ref.current)
                }
                fullWidth>
                <Button.Icon icon={<PiCheck />} />
                <Button.Text text={confirmLabel} />
              </Button>
            )}
            <Button
              isClear
              fullWidth
              variant={BUTTON_VARIANT.remove}
              onClick={() => (onClose ? onClose() : ref.current?.close())}>
              <Button.Icon icon={<PiX />} />
              <Button.Text
                text={onConfirm ? cancelLabel : closeLabel}
              />
            </Button>
          </div>
        </MagellanPopup>
      </div>
    </Popup>
  );
};

export default CriticalWarningPopup;
